import { useState, useEffect } from 'react';

const EditPermissionModal = ({ permission, onClose, onUpdate }) => {
    const [formData, setFormData] = useState({
        addSection: '',
        features: [{ value: '', enabled: false, subfeatures: [{ value: '', enabled: false }] }],
    });

    useEffect(() => {
        if (permission) {
            setFormData({
                addSection: permission.section,
                features: permission.feature.map(feat => ({
                    value: feat.value,
                    enabled: feat.enabled,
                    subfeatures: feat.subfeatures && feat.subfeatures.length > 0 ?
                        feat.subfeatures.map(sub => ({
                            value: sub.value,
                            enabled: sub.enabled
                        })) :
                        [{ value: '', enabled: false }]
                }))
            });
        }
    }, [permission]);

    const modalStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
    };

    const modalContentStyle = {
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        width: '600px',
        maxHeight: '80vh',
        overflowY: 'auto',
        textAlign: 'center',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    };

    const inputStyle = {
        padding: '10px',
        width: '50%',
        borderRadius: '4px',
        border: '1px solid #ccc',
        margin: '10px 5px 10px 0',
    };

    const buttonStyle = {
        padding: '10px 20px',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#007bff',
        color: '#fff',
        cursor: 'pointer',
        marginTop: '10px',
        marginRight: '10px',
    };

    const smallButtonStyle = {
        padding: '5px 10px',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#007bff',
        color: '#fff',
        cursor: 'pointer',
        marginLeft: '5px',
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleFeatureChange = (index, field, value) => {
        const updatedFeatures = [...formData.features];
        if (field === 'value') {
            updatedFeatures[index].value = value;
        } else if (field === 'enabled') {
            updatedFeatures[index].enabled = value;
        }
        setFormData({ ...formData, features: updatedFeatures });
    };

    const handleAddFeature = () => {
        setFormData((prevData) => ({
            ...prevData,
            features: [...prevData.features, {
                value: '',
                enabled: false,
                subfeatures: [{ value: '', enabled: false }]
            }],
        }));
    };

    const handleRemoveFeature = (index) => {
        if (formData.features.length > 1) {
            const updatedFeatures = formData.features.filter((_, i) => i !== index);
            setFormData({ ...formData, features: updatedFeatures });
        } else {
            alert('At least one feature is required');
        }
    };

    const handleSubfeatureChange = (featureIndex, subfeatureIndex, field, value) => {
        const updatedFeatures = [...formData.features];
        if (field === 'value') {
            updatedFeatures[featureIndex].subfeatures[subfeatureIndex].value = value;
        } else if (field === 'enabled') {
            updatedFeatures[featureIndex].subfeatures[subfeatureIndex].enabled = value;
        }
        setFormData({ ...formData, features: updatedFeatures });
    };

    const handleAddSubfeature = (featureIndex) => {
        const updatedFeatures = [...formData.features];
        updatedFeatures[featureIndex].subfeatures.push({ value: '', enabled: false });
        setFormData({ ...formData, features: updatedFeatures });
    };

    const handleRemoveSubfeature = (featureIndex, subfeatureIndex) => {
        const updatedFeatures = [...formData.features];
        updatedFeatures[featureIndex].subfeatures = updatedFeatures[featureIndex].subfeatures.filter((_, i) => i !== subfeatureIndex);
        setFormData({ ...formData, features: updatedFeatures });

    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate form data
        if (!formData.addSection.trim()) {
            alert('Section name is required');
            return;
        }

        // Validate features
        const hasEmptyFeature = formData.features.some(feature => !feature.value.trim());
        if (hasEmptyFeature) {
            alert('All features must have a name');
            return;
        }

        // Validate subfeatures
        const hasEmptySubfeature = formData.features.some(feature =>
            feature.subfeatures.some(subfeature => !subfeature.value.trim())
        );
        if (hasEmptySubfeature) {
            alert('All subfeatures must have a name');
            return;
        }

        // Prepare data for API
        const submitData = {
            addSection: formData.addSection.trim(),
            features: formData.features.map(feature => ({
                value: feature.value.trim(),
                enabled: feature.enabled,
                subfeatures: feature.subfeatures.map(subfeature => ({
                    value: subfeature.value.trim(),
                    enabled: subfeature.enabled
                }))
            }))
        };

        onUpdate(submitData);
    };

    return (
        <div style={modalStyle}>
            <div style={modalContentStyle}>
                <h2>Edit Permission</h2>
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Section Name:
                        </label>
                        <input
                            type="text"
                            name="addSection"
                            value={formData.addSection}
                            onChange={handleChange}
                            placeholder="Enter section name"
                            style={inputStyle}
                            required
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <label style={{ fontWeight: 'bold' }}>Features:</label>
                            <button
                                type="button"
                                onClick={handleAddFeature}
                                style={smallButtonStyle}
                            >
                                Add Feature
                            </button>
                        </div>

                        {formData.features.map((feature, featureIndex) => (
                            <div key={featureIndex} style={{
                                border: '1px solid #ddd',
                                borderRadius: '5px',
                                padding: '15px',
                                marginBottom: '15px',
                                backgroundColor: '#f9f9f9'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                    <input
                                        type="text"
                                        value={feature.value}
                                        onChange={(e) => handleFeatureChange(featureIndex, 'value', e.target.value)}
                                        placeholder={`Feature name`}
                                        style={{ ...inputStyle, width: '60%' }}
                                        required
                                    />
                                    <label style={{ marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={feature.enabled}
                                            onChange={(e) => handleFeatureChange(featureIndex, 'enabled', e.target.checked)}
                                            style={{ marginRight: '5px' }}
                                        />
                                        Enabled
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFeature(featureIndex)}
                                        style={{ ...smallButtonStyle, backgroundColor: '#dc3545', marginLeft: '10px' }}
                                    >
                                        Remove
                                    </button>
                                </div>

                                <div style={{ marginLeft: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <label style={{ fontWeight: 'bold' }}>Subfeatures:</label>
                                        <button
                                            type="button"
                                            onClick={() => handleAddSubfeature(featureIndex)}
                                            style={{ ...smallButtonStyle, fontSize: '12px' }}
                                        >
                                            Add Subfeature
                                        </button>
                                    </div>

                                    {feature.subfeatures.map((subfeature, subfeatureIndex) => (
                                        <div key={subfeatureIndex} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '8px',
                                            padding: '8px',
                                            backgroundColor: '#fff',
                                            borderRadius: '3px'
                                        }}>
                                            <input
                                                type="text"
                                                value={subfeature.value}
                                                onChange={(e) => handleSubfeatureChange(featureIndex, subfeatureIndex, 'value', e.target.value)}
                                                placeholder={`Subfeature name`}
                                                style={{ ...inputStyle, width: '50%', marginRight: '10px' }}
                                                required
                                            />
                                            <label style={{ display: 'flex', alignItems: 'center', marginRight: '10px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={subfeature.enabled}
                                                    onChange={(e) => handleSubfeatureChange(featureIndex, subfeatureIndex, 'enabled', e.target.checked)}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Enabled
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSubfeature(featureIndex, subfeatureIndex)}
                                                style={{ ...smallButtonStyle, backgroundColor: '#dc3545', fontSize: '12px' }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div>
                        <button type="submit" style={buttonStyle}>
                            Update Permission
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{ ...buttonStyle, backgroundColor: '#6c757d' }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditPermissionModal;